/**
 * Conversation Status Types
 * Defines the status system for conversations separate from the conversation state
 */

// Status represents business workflow state
export type ConversationStatus = 
  | 'open' 
  | 'action_required' 
  | 'in_progress' 
  | 'closed' 
  | 'archived';

// Status display configuration
export interface StatusConfig {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
  icon?: string;
}

// Status configuration mapping
export const statusConfig: Record<ConversationStatus, StatusConfig> = {
  open: {
    label: 'Open',
    variant: 'default',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: 'circle'
  },
  action_required: {
    label: 'Action Required',
    variant: 'destructive',
    className: 'bg-red-100 text-red-800 border-red-200',
    icon: 'alert-triangle'
  },
  in_progress: {
    label: 'In Progress',
    variant: 'default',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: 'clock'
  },
  closed: {
    label: 'Closed',
    variant: 'secondary',
    className: 'bg-green-100 text-green-800 border-green-200',
    icon: 'check-circle'
  },
  archived: {
    label: 'Archived',
    variant: 'outline',
    className: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: 'archive'
  }
};

// Default statuses to show in filters (active conversations)
export const defaultVisibleStatuses: ConversationStatus[] = [
  'open',
  'action_required', 
  'in_progress'
];

// All available status options
export const allStatuses: ConversationStatus[] = [
  'open',
  'action_required',
  'in_progress', 
  'closed',
  'archived'
];

// Helper function to get status configuration
export function getStatusConfig(status: ConversationStatus | null | undefined): StatusConfig {
  if (!status || !statusConfig[status]) {
    return statusConfig.open; // Default fallback
  }
  return statusConfig[status];
}

// Helper function to check if status should be visible by default
export function isStatusVisibleByDefault(status: ConversationStatus | null | undefined): boolean {
  if (!status) return true; // Show open conversations by default
  return defaultVisibleStatuses.includes(status);
}

// Validation function for status values
export function isValidStatus(status: string): status is ConversationStatus {
  return allStatuses.includes(status as ConversationStatus);
} 